import React, { useState, useContext, useMemo } from "react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { WebTemplateContext } from "../context/WebTemplateContext";
import { CreatorContext } from "../context/CreatorContext";
import { useTranslation } from "react-i18next";
import { getTextColor } from "../utils/getColors";

const ECOMMERCE_TEMPLATES = new Set([
	"drugstore",
	"shop1",
	"shop2",
	"shop3",
	"shop4",
	"shop5",
	"shop6",
	"gaps",
]);

export default function CookieBanner() {
	const { decided, acceptAll, rejectAll, updateConsent } = useCookieConsent();
	const { webTemplate } = useContext(WebTemplateContext);
	const { creator } = useContext(CreatorContext);
	const { t } = useTranslation();
	const [showSettings, setShowSettings] = useState(false);
	const [localThirdParty, setLocalThirdParty] = useState(false);

	const isEcommerce = useMemo(() => {
		const appType = creator?.app?.appType?.toLowerCase();
		const template = creator?.webTemplate?.toLowerCase();
		return (
			appType === "ecommerce" ||
			appType === "woocommerce" ||
			ECOMMERCE_TEMPLATES.has(template)
		);
	}, [creator?.app?.appType, creator?.webTemplate]);

	const cookieMessage = isEcommerce
		? t("cookies.messageEcommerce")
		: t("cookies.message");
	const thirdPartyDesc = isEcommerce
		? t("cookies.thirdPartyDescEcommerce")
		: t("cookies.thirdPartyDesc");

	if (decided) return null;

	const mainColor = webTemplate?.mainColor || "#43BD98";
	const onMainText = getTextColor(mainColor);

	return (
		<div
			className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
			style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
		>
			<div
				className="max-w-3xl mx-auto rounded-xl p-5 sm:p-6 shadow-2xl"
				style={{ backgroundColor: "#ffffff", color: "#1a1a1a" }}
			>
				{!showSettings ? (
					<>
						<p className="text-sm sm:text-base leading-relaxed mb-4">
							{cookieMessage}
						</p>
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
							<button
								onClick={acceptAll}
								className="px-5 py-2.5 text-sm font-medium rounded-lg transition-opacity hover:opacity-90"
								style={{ backgroundColor: mainColor, color: onMainText }}
							>
								{t("cookies.acceptAll")}
							</button>
							<button
								onClick={rejectAll}
								className="px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50"
								style={{ borderColor: "#d1d5db", color: "#374151" }}
							>
								{t("cookies.rejectAll")}
							</button>
							<button
								onClick={() => setShowSettings(true)}
								className="px-5 py-2.5 text-sm font-medium rounded-lg transition-colors hover:bg-gray-50 underline underline-offset-2"
								style={{ color: "#6b7280" }}
							>
								{t("cookies.settings")}
							</button>
						</div>
					</>
				) : (
					<>
						<h3 className="text-base font-semibold mb-3">
							{t("cookies.settingsTitle")}
						</h3>

						<div className="space-y-3 mb-5">
							<div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
								<div>
									<p className="text-sm font-medium">{t("cookies.necessary")}</p>
									<p className="text-xs text-gray-500">{t("cookies.necessaryDesc")}</p>
								</div>
								<span className="text-xs font-medium text-gray-400 uppercase">
									{t("cookies.alwaysActive")}
								</span>
							</div>

							<div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
								<div>
									<p className="text-sm font-medium">{t("cookies.thirdParty")}</p>
									<p className="text-xs text-gray-500">{thirdPartyDesc}</p>
								</div>
								<button
									type="button"
									role="switch"
									aria-checked={localThirdParty}
									onClick={() => setLocalThirdParty(!localThirdParty)}
									className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
									style={{ backgroundColor: localThirdParty ? mainColor : "#d1d5db" }}
								>
									<span
										className="inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm"
										style={{ transform: localThirdParty ? "translateX(22px)" : "translateX(4px)" }}
									/>
								</button>
							</div>
						</div>

						<div className="flex flex-col sm:flex-row gap-2">
							<button
								onClick={() => updateConsent({ thirdParty: localThirdParty })}
								className="px-5 py-2.5 text-sm font-medium rounded-lg transition-opacity hover:opacity-90"
								style={{ backgroundColor: mainColor, color: onMainText }}
							>
								{t("cookies.saveSettings")}
							</button>
							<button
								onClick={() => setShowSettings(false)}
								className="px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50"
								style={{ borderColor: "#d1d5db", color: "#374151" }}
							>
								{t("cookies.back")}
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
